import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faPrint, faEye } from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import { productService } from "../services/product";
import { productParametersService } from "../services/productParameters";
import Layout from "../components/Layout";
import Chart from "react-apexcharts";
import { authService } from "../services/auth"; // Importar authService
import ParameterFormModal from "../components/ParameterFormModal";
import { formatDateForDisplay } from "../utils/dateUtils";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [productDetails, setProductDetails] = useState(null);
  const [productionDemandData, setProductionDemandData] = useState(null);
  const [stockMovementData, setStockMovementData] = useState(null);
  const [lossVolumeData, setLossVolumeData] = useState(null);
  const [lotHistory, setLotHistory] = useState([]);
  const [availableLots, setAvailableLots] = useState([]);
  const [closedLots, setClosedLots] = useState([]);
  const [lotMovements, setLotMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null); // Estado para o usuário logado

  // Visualização de parâmetros preenchidos de um lote fechado (somente leitura)
  const [viewingLotParams, setViewingLotParams] = useState(null); // lote selecionado
  const [lotParamResponses, setLotParamResponses] = useState(null);
  const [loadingLotParams, setLoadingLotParams] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      const user = await authService.getCurrentUser();
      setCurrentUser(user);
    };
    fetchUser();
  }, []);

  const productionDemandChartRef = useRef(null); // Ref para o DOM
  const stockMovementChartRef = useRef(null); // Ref para o DOM
  const lossVolumeChartRef = useRef(null); // Ref para o DOM

  const [productionDemandPeriod, setProductionDemandPeriod] =
    useState("this_year");
  const [stockMovementPeriod, setStockMovementPeriod] = useState("7_days");
  const [lossVolumePeriod, setLossVolumePeriod] = useState("this_year");
  const [lotHistoryPeriod, setLotHistoryPeriod] = useState("last_10_lots");

  // Funções de fetch separadas
  const fetchProductDetails = async (productId) => {
    try {
      const product = await productService.getProductById(productId);
      setProductDetails(product);
    } catch (err) {
      setError(err.message || "Erro ao carregar detalhes do produto.");
      console.error("Erro ao carregar detalhes do produto:", err);
    }
  };

  const fetchProductionDemandData = async (productId, period) => {
    try {
      const prodDemand = await productService.getProductProductionDemand(
        productId,
        period
      );
      setProductionDemandData(prodDemand);
    } catch (err) {
      setError(err.message || "Erro ao carregar dados de produção e demanda.");
      console.error("Erro ao carregar dados de produção e demanda:", err);
    }
  };

  const fetchStockMovementData = async (productId, period) => {
    try {
      const stockMov = await productService.getProductStockMovement(
        productId,
        period
      );
      setStockMovementData(stockMov);
    } catch (err) {
      setError(
        err.message || "Erro ao carregar dados de stock e movimentação."
      );
      console.error("Erro ao carregar dados de stock e movimentação:", err);
    }
  };

  const fetchLossVolumeData = async (productId, period) => {
    try {
      const lossVol = await productService.getProductLossVolume(
        productId,
        period
      );
      setLossVolumeData(lossVol);
    } catch (err) {
      setError(err.message || "Erro ao carregar dados de volume de perdas.");
      console.error("Erro ao carregar dados de volume de perdas:", err);
    }
  };

  const fetchLotHistory = async (productId, limit) => {
    try {
      const lotHist = await productService.getProductLotHistory(
        productId,
        limit
      );
      setLotHistory(Array.isArray(lotHist) ? lotHist : []);
    } catch (err) {
      console.error("Erro ao carregar histórico de lotes:", err);
      setLotHistory([]);
    }
  };

  const fetchAvailableLots = async (productId) => {
    try {
      const lots = await productService.getProductAvailableLots(productId);
      setAvailableLots(Array.isArray(lots) ? lots : []);
    } catch (err) {
      console.error("Erro ao carregar lotes disponíveis:", err);
      setAvailableLots([]);
    }
  };

  const fetchLotMovements = async (productId) => {
    try {
      const movements = await productService.getProductLotMovements(productId);
      setLotMovements(Array.isArray(movements) ? movements : []);
    } catch (err) {
      console.error("Erro ao carregar movimentações dos lotes:", err);
      setLotMovements([]);
    }
  };

  const fetchClosedLots = async (productId) => {
    try {
      const lots = await productService.getProductClosedLots(productId);
      setClosedLots(Array.isArray(lots) ? lots : []);
    } catch (err) {
      console.error("Erro ao carregar lotes fechados:", err);
      setClosedLots([]);
    }
  };

  const handleViewLotParameters = async (lot) => {
    setViewingLotParams(lot);
    setLotParamResponses(null);
    setLoadingLotParams(true);
    try {
      const data = await productParametersService.getResponsesByLot(lot.id);
      if (!data) {
        toast.error("Este lote não tem parâmetros preenchidos.");
        setViewingLotParams(null);
        return;
      }
      setLotParamResponses(data);
    } catch (err) {
      console.error("Erro ao buscar parâmetros do lote:", err);
      toast.error(err.message || "Erro ao buscar parâmetros do lote");
      setViewingLotParams(null);
    } finally {
      setLoadingLotParams(false);
    }
  };

  // useEffect para carregar dados iniciais e quando o ID do produto muda
  useEffect(() => {
    if (id) {
      setLoading(true);
      Promise.all([
        fetchProductDetails(id),
        fetchProductionDemandData(id, productionDemandPeriod),
        fetchStockMovementData(id, stockMovementPeriod),
        fetchLossVolumeData(id, lossVolumePeriod),
        fetchLotHistory(id, lotHistoryPeriod),
        fetchAvailableLots(id),
        fetchClosedLots(id),
        fetchLotMovements(id),
      ]).finally(() => setLoading(false));
    }
  }, [
    id,
    productionDemandPeriod,
    stockMovementPeriod,
    lossVolumePeriod,
    lotHistoryPeriod,
  ]); // Adicionadas dependências de período

  // useEffects para recarregar dados quando os períodos/limites mudam
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (id && !loading) {
      // Só recarrega se não estiver carregando inicialmente
      fetchProductionDemandData(id, productionDemandPeriod);
    }
  }, [id, productionDemandPeriod]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (id && !loading) {
      fetchStockMovementData(id, stockMovementPeriod);
    }
  }, [id, stockMovementPeriod]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (id && !loading) {
      fetchLossVolumeData(id, lossVolumePeriod);
    }
  }, [id, lossVolumePeriod]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (id && !loading) {
      fetchLotHistory(id, lotHistoryPeriod);
    }
  }, [id, lotHistoryPeriod]);

  // useEffect unificado para gerenciar os gráficos
  const getProductionDemandChartOptions = () => ({
    series: productionDemandData.series,
    chart: { type: "bar", height: 300, toolbar: { show: false } },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "55%", endingShape: "rounded" },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: { categories: productionDemandData.labels },
    yaxis: { title: { text: "Quantidade (kg)" } },
    fill: { opacity: 1 },
    tooltip: { y: { formatter: (val) => `${val} kg` } },
    colors: ["#1f2937", "#9ca3af"],
  });

  const getStockMovementChartOptions = () => ({
    series: stockMovementData.series,
    chart: { type: "line", height: 300, toolbar: { show: false } },
    stroke: { curve: "smooth" },
    xaxis: { categories: stockMovementData.labels },
    yaxis: { title: { text: "Quantidade (kg)" } },
    colors: ["#3b82f6"],
  });

  const getLossVolumeChartOptions = () => ({
    series: lossVolumeData.series,
    chart: { type: "bar", height: 300, toolbar: { show: false } },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "55%", endingShape: "rounded" },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: { categories: lossVolumeData.labels },
    yaxis: { title: { text: "Quantidade (kg)" } },
    fill: { opacity: 1 },
    tooltip: { y: { formatter: (val) => `${val} kg` } },
    colors: ["#ef4444"],
  });

  const handleProductionDemandPeriodChange = (e) => {
    setProductionDemandPeriod(e.target.value);
    fetchProductionDemandData(id, e.target.value);
  };

  const handleStockMovementPeriodChange = (e) => {
    setStockMovementPeriod(e.target.value);
    fetchStockMovementData(id, e.target.value);
  };

  const handleLossVolumePeriodChange = (e) => {
    setLossVolumePeriod(e.target.value);
    fetchLossVolumeData(id, e.target.value);
  };

  const handleLotHistoryPeriodChange = (e) => {
    setLotHistoryPeriod(e.target.value);
    fetchLotHistory(id, e.target.value);
  };

  // Função para traduzir tipos de movimento
  const getMovementTypeLabel = (type) => {
    const types = {
      'expedition_out': 'Expedição',
      'loss': 'Quebra',
      'production_in': 'Produção',
      'manual_withdrawal_out': 'Quebra Manual',
      'initial_stock': 'Estoque Inicial',
      'adjustment': 'Ajuste',
      'transfer_in': 'Transferência Entrada',
      'transfer_out': 'Transferência Saída'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 text-center">
        A carregar detalhes do produto...
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 text-center text-red-600">
        {error}
      </div>
    );
  }

  // Verificações de dados para renderização condicional
  const hasProductionDemandData =
    productionDemandData?.series?.[0]?.data?.length > 0;
  const hasStockMovementData = stockMovementData?.series?.[0]?.data?.length > 0;
  const hasLossVolumeData = lossVolumeData?.series?.[0]?.data?.length > 0;
  const hasLotHistory = lotHistory.length > 0;

  return (
    <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
          <button
            className="text-neutral-600 hover:text-neutral-900"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg sm:text-xl" />
          </button>
          <h1 className="text-lg sm:text-2xl font-semibold truncate">
            {productDetails?.name || "Detalhes do Produto"}
          </h1>
        </div>
        <button className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 text-sm sm:text-base">
          <FontAwesomeIcon icon={faPrint} className="mr-2" />
          Exportar Relatório
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Bloco Produção vs Demanda */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4 space-y-2 sm:space-y-0">
            <h2 className="text-base sm:text-lg font-semibold">Produção vs Demanda</h2>
            <select
              className="w-full sm:w-auto px-2 py-1 text-xs sm:text-sm border border-neutral-200 rounded-lg"
              value={productionDemandPeriod}
              onChange={handleProductionDemandPeriodChange}
            >
              <option value="this_year">Este Ano</option>
              <option value="last_6_months">Últimos 6 meses</option>
              <option value="last_3_months">Últimos 3 meses</option>
              <option value="last_month">Último mês</option>
            </select>
          </div>
          <div className="bg-neutral-50 h-[250px] sm:h-[300px] rounded-lg p-2 sm:p-4">
            {hasProductionDemandData ? (
              <Chart
                options={getProductionDemandChartOptions()}
                series={productionDemandData.series}
                type="bar"
                height={window.innerWidth < 640 ? 250 : 300}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-neutral-500 text-xs sm:text-sm">
                Nenhum dado de produção ou demanda encontrado para este período.
              </div>
            )}
          </div>
          <div className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
            <div className="bg-neutral-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-neutral-600">Produção Mensal</div>
              <div className="text-lg sm:text-xl mt-1 font-semibold">
                {productionDemandData?.monthlyProduction || "0.00"}
              </div>
            </div>
            <div className="bg-neutral-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-neutral-600">Demanda pra venda</div>
              <div className="text-lg sm:text-xl mt-1 font-semibold">
                {productionDemandData?.monthlyDemand || "0.00"}
              </div>
            </div>
            <div className="bg-neutral-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-neutral-600">Ineficiência</div>
              <div className="text-lg sm:text-xl mt-1 font-semibold">
                {productionDemandData?.efficiency ? (100 - parseFloat(productionDemandData.efficiency)).toFixed(1) : "N/A"}%
              </div>
            </div>
          </div>
        </div>

        {/* Bloco Stock e Movimentação */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4 space-y-2 sm:space-y-0">
            <h2 className="text-base sm:text-lg font-semibold">Stock e Movimentação</h2>
            <select
              className="w-full sm:w-auto px-2 py-1 text-xs sm:text-sm border border-neutral-200 rounded-lg"
              value={stockMovementPeriod}
              onChange={handleStockMovementPeriodChange}
            >
              <option value="7_days">7 dias</option>
              <option value="30_days">30 dias</option>
              <option value="90_days">90 dias</option>
            </select>
          </div>
          <div className="bg-neutral-50 h-[250px] sm:h-[300px] rounded-lg p-2 sm:p-4">
            {hasStockMovementData ? (
              <Chart
                options={getStockMovementChartOptions()}
                series={stockMovementData.series}
                type="line"
                height={window.innerWidth < 640 ? 250 : 300}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-neutral-500 text-xs sm:text-sm">
                Nenhuma movimentação de stock encontrada para este período.
              </div>
            )}
          </div>
          <div className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
            <div className="bg-neutral-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-neutral-600">Stock Atual</div>
              <div className="text-lg sm:text-xl mt-1 font-semibold">
                {stockMovementData?.currentStock || "0.00"}
              </div>
            </div>
            <div className="bg-neutral-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-neutral-600">Quebra de estoque</div>
              <div className="text-lg sm:text-xl mt-1 font-semibold">
                {stockMovementData?.monthlyAverage || "0.00"}
              </div>
            </div>
            <div className="bg-neutral-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-neutral-600">Rotatividade</div>
              <div className="text-lg sm:text-xl mt-1 font-semibold">
                {stockMovementData?.turnover || "N/A"}
              </div>
            </div>
          </div>
        </div>

        {/* Bloco Volume de Perdas */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4 space-y-2 sm:space-y-0">
            <h2 className="text-base sm:text-lg font-semibold">Volume de Perdas</h2>
            <select
              className="w-full sm:w-auto px-2 py-1 text-xs sm:text-sm border border-neutral-200 rounded-lg"
              value={lossVolumePeriod}
              onChange={handleLossVolumePeriodChange}
            >
              <option value="last_month">Último mês</option>
              <option value="last_3_months">Últimos 3 meses</option>
              <option value="last_6_months">Últimos 6 meses</option>
              <option value="this_year">Este ano</option>
            </select>
          </div>
          <div className="bg-neutral-50 h-[250px] sm:h-[300px] rounded-lg p-2 sm:p-4">
            {hasLossVolumeData ? (
              <Chart
                options={getLossVolumeChartOptions()}
                series={lossVolumeData.series}
                type="bar"
                height={window.innerWidth < 640 ? 250 : 300}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-neutral-500 text-xs sm:text-sm">
                Nenhum dado de perdas encontrado para este período.
              </div>
            )}
          </div>
          <div className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
            <div className="bg-neutral-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-neutral-600">Perdas Mensal</div>
              <div className="text-lg sm:text-xl mt-1 font-semibold">
                {lossVolumeData?.monthlyLoss || "0.00"}
              </div>
            </div>
            <div className="bg-neutral-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-neutral-600">% de Perdas</div>
              <div className="text-lg sm:text-xl mt-1 font-semibold">
                {lossVolumeData?.lossPercentage || "0.00"}%
              </div>
            </div>
            <div className="bg-neutral-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-neutral-600">Valor Perdido</div>
              <div className="text-lg sm:text-xl mt-1 font-semibold">
                {lossVolumeData?.lostValue || "N/A"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bloco Lotes Disponíveis */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Lotes Disponíveis</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Nº Lote</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Data Produção</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Qtd. Inicial</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Qtd. Disponível</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Status</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Qualidade</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Parâmetros</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {availableLots.filter(lot => lot.available_quantity > 0).length > 0 ? (
                availableLots.filter(lot => lot.available_quantity > 0).map((lot) => (
                  <tr key={lot.id}>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium">{lot.lot_number}</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{formatDateForDisplay(lot.production_date)}</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{lot.initial_quantity} kg</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-blue-600">{lot.available_quantity} kg</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        lot.status === 'available' ? 'bg-green-100 text-green-800' :
                        lot.status === 'curing' ? 'bg-yellow-100 text-yellow-800' :
                        lot.status === 'finished' ? 'bg-gray-100 text-gray-800' :
                        'bg-neutral-100 text-neutral-800'
                      }`}>
                        {lot.status === 'available' ? 'Disponível' :
                         lot.status === 'curing' ? 'Em Cura' :
                         lot.status === 'finished' ? 'Finalizado' :
                         lot.status}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                      <span className={`px-2 py-1 rounded ${
                        lot.quality_grade === 'A' ? 'bg-green-100 text-green-800' :
                        lot.quality_grade === 'B' ? 'bg-blue-100 text-blue-800' :
                        lot.quality_grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {lot.quality_grade}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                      <button
                        onClick={() => handleViewLotParameters(lot)}
                        disabled={loadingLotParams && viewingLotParams?.id === lot.id}
                        className="px-3 py-1.5 text-xs sm:text-sm text-purple-600 hover:bg-purple-50 rounded disabled:opacity-50"
                        title="Ver parâmetros preenchidos deste lote"
                      >
                        <FontAwesomeIcon icon={faEye} className="mr-1" />
                        Ver Parâmetros
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-neutral-500 text-xs sm:text-sm">
                    Nenhum lote disponível para este produto.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bloco Lotes Fechados (esgotados) */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6 mt-6">
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Lotes Fechados</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Nº Lote</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Data Produção</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Data Fechamento</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Qtd. Inicial</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Qualidade</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Parâmetros</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {closedLots.length > 0 ? (
                closedLots.map((lot) => (
                  <tr key={lot.id}>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium">{lot.lot_number}</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{formatDateForDisplay(lot.production_date)}</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                      {lot.closed_at ? formatDateForDisplay(lot.closed_at) : '-'}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{lot.initial_quantity} kg</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                      <span className={`px-2 py-1 rounded ${
                        lot.quality_grade === 'A' ? 'bg-green-100 text-green-800' :
                        lot.quality_grade === 'B' ? 'bg-blue-100 text-blue-800' :
                        lot.quality_grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {lot.quality_grade}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                      <button
                        onClick={() => handleViewLotParameters(lot)}
                        disabled={loadingLotParams && viewingLotParams?.id === lot.id}
                        className="px-3 py-1.5 text-xs sm:text-sm text-purple-600 hover:bg-purple-50 rounded disabled:opacity-50"
                        title="Ver parâmetros preenchidos deste lote"
                      >
                        <FontAwesomeIcon icon={faEye} className="mr-1" />
                        Ver Parâmetros
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-neutral-500 text-xs sm:text-sm">
                    Nenhum lote fechado para este produto.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bloco Histórico de Movimentações dos Lotes */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6 mt-6">
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Histórico de Movimentações dos Lotes</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Data/Hora</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Nº Lote</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Tipo</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Descrição</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Qtd. Antes</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Qtd. Depois</th>
                <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Diferença</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {lotMovements.length > 0 ? (
                lotMovements.map((movement) => (
                  <tr key={movement.id}>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                      {new Date(movement.movement_date).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium">
                      {movement.lot_number || '-'}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        movement.movement_type === 'expedition_out' ? 'bg-blue-100 text-blue-800' :
                        movement.movement_type === 'loss' || movement.movement_type === 'manual_withdrawal_out' ? 'bg-red-100 text-red-800' :
                        movement.movement_type === 'production_in' || movement.movement_type === 'initial_stock' ? 'bg-green-100 text-green-800' :
                        'bg-neutral-100 text-neutral-800'
                      }`}>
                        {getMovementTypeLabel(movement.movement_type)}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{movement.description || movement.notes || '-'}</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{movement.quantity_before} kg</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{movement.quantity_after} kg</td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                      <span className={`font-semibold ${
                        movement.quantity_after < movement.quantity_before ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {movement.quantity_after < movement.quantity_before ? '-' : '+'}{Math.abs(movement.quantity_after - movement.quantity_before).toFixed(2)} kg
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-neutral-500 text-xs sm:text-sm">
                    Nenhuma movimentação encontrada para este produto.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de visualização dos parâmetros preenchidos de um lote fechado */}
      {viewingLotParams && lotParamResponses && (
        <ParameterFormModal
          isOpen={true}
          onClose={() => {
            setViewingLotParams(null);
            setLotParamResponses(null);
          }}
          productId={id}
          productName={`${productDetails?.name || ""} — Lote ${viewingLotParams.lot_number}`}
          existingResponses={lotParamResponses}
          readOnly={true}
        />
      )}
    </div>
  );
};

export default ProductDetails;
